import React, { useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import SEO from '../components/SEO';
import {
    Box,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Snackbar,
    Alert,
    Typography,
    Chip,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import AuthContext from '../context/AuthContext';
import dayjs from 'dayjs';

const USER_COLOR = '#7B6B8D';
const PARTNER_COLOR = '#C4856A';
const TIME_RANGES_FREE = ['Week', 'Month', '3 Months', '6 Months', 'Year'];
const TIME_RANGES_PRO = ['Week', 'Month', '3 Months', '6 Months', 'Year', 'All'];
const MULTI_MONTH_RANGES = new Set(['3 Months', '6 Months', 'Year', 'All']);

const ChoreComparisonRow = ({ chore, partnerLabel, onShowMonthly }) => {
    const total = (chore.user_value || 0) + (chore.partner_value || 0);
    const userPct = total > 0 ? ((chore.user_value || 0) / total) * 100 : 50;
    const partnerPct = total > 0 ? ((chore.partner_value || 0) / total) * 100 : 50;
    const unit = chore.chore_type === 2 ? 'h' : 'x';

    return (
        <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Typography sx={{ fontWeight: 500, color: '#222', fontSize: '0.9rem' }}>
                    {chore.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography sx={{ color: USER_COLOR, fontWeight: 700, fontSize: '0.85rem' }}>
                        {chore.user_value || 0}{unit}
                    </Typography>
                    <Typography sx={{ color: '#bbb', fontSize: '0.75rem' }}>/</Typography>
                    <Typography sx={{ color: PARTNER_COLOR, fontWeight: 700, fontSize: '0.85rem' }}>
                        {chore.partner_value || 0}{unit}
                    </Typography>
                    {onShowMonthly && (
                        <IconButton size="small" onClick={() => onShowMonthly(chore.chore_id, chore.name, 'chore')} sx={{ p: 0.25, ml: 0.25 }}>
                            <BarChartOutlinedIcon sx={{ fontSize: '1rem', color: '#bbb', '&:hover': { color: USER_COLOR } }} />
                        </IconButton>
                    )}
                </Box>
            </Box>
            <Box sx={{
                display: 'flex',
                height: 8,
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: '#f0eef3',
            }}>
                {userPct > 0 && (
                    <Box sx={{ width: `${userPct}%`, bgcolor: USER_COLOR, transition: 'width 0.4s ease' }} />
                )}
                {partnerPct > 0 && (
                    <Box sx={{ width: `${partnerPct}%`, bgcolor: PARTNER_COLOR, transition: 'width 0.4s ease' }} />
                )}
            </Box>
        </Box>
    );
};

const ScoreCard = ({ label, value, unit, color }) => (
    <Box sx={{
        flex: 1,
        bgcolor: color,
        borderRadius: 2,
        p: { xs: 2.5, md: 3 },
        textAlign: 'center',
        color: 'white',
    }}>
        <Typography sx={{ opacity: 0.85, fontSize: '0.85rem', mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '2.25rem', lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ opacity: 0.75, fontSize: '0.75rem', mt: 0.5 }}>{unit}</Typography>
    </Box>
);

const TimeoffComparisonRow = ({ timeoff, partnerLabel, onShowMonthly }) => {
    const total = (timeoff.user_value || 0) + (timeoff.partner_value || 0);
    const userPct = total > 0 ? ((timeoff.user_value || 0) / total) * 100 : 50;
    const partnerPct = total > 0 ? ((timeoff.partner_value || 0) / total) * 100 : 50;

    return (
        <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Typography sx={{ fontWeight: 500, color: '#222', fontSize: '0.9rem' }}>
                    {timeoff.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography sx={{ color: USER_COLOR, fontWeight: 700, fontSize: '0.85rem' }}>
                        {timeoff.user_value || 0}h
                    </Typography>
                    <Typography sx={{ color: '#bbb', fontSize: '0.75rem' }}>/</Typography>
                    <Typography sx={{ color: PARTNER_COLOR, fontWeight: 700, fontSize: '0.85rem' }}>
                        {timeoff.partner_value || 0}h
                    </Typography>
                    {onShowMonthly && (
                        <IconButton size="small" onClick={() => onShowMonthly(timeoff.timeoff_id, timeoff.name, 'timeoff')} sx={{ p: 0.25, ml: 0.25 }}>
                            <BarChartOutlinedIcon sx={{ fontSize: '1rem', color: '#bbb', '&:hover': { color: USER_COLOR } }} />
                        </IconButton>
                    )}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: '#f0eef3' }}>
                {userPct > 0 && <Box sx={{ width: `${userPct}%`, bgcolor: USER_COLOR, transition: 'width 0.4s ease' }} />}
                {partnerPct > 0 && <Box sx={{ width: `${partnerPct}%`, bgcolor: PARTNER_COLOR, transition: 'width 0.4s ease' }} />}
            </Box>
        </Box>
    );
};

const MonthlyDetailDialog = ({ open, onClose, title, data, loading, partnerLabel, showPartner }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>{title} — Monthly</Typography>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: USER_COLOR }} size={28} />
                </Box>
            ) : data.length === 0 ? (
                <Typography sx={{ color: '#aaa', textAlign: 'center', py: 3 }}>No data for this period</Typography>
            ) : (
                <>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: USER_COLOR }} />
                            <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>You</Typography>
                        </Box>
                        {showPartner && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: PARTNER_COLOR }} />
                                <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>{partnerLabel}</Typography>
                            </Box>
                        )}
                    </Box>
                    <BarChart
                        height={220}
                        series={[
                            { data: data.map(d => d.user_value), label: 'You', color: USER_COLOR, id: 'user' },
                            ...(showPartner ? [{ data: data.map(d => d.partner_value), label: partnerLabel, color: PARTNER_COLOR, id: 'partner' }] : []),
                        ]}
                        xAxis={[{ data: data.map(d => d.month), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                        yAxis={[{ tickLabelStyle: { fontSize: 11 } }]}
                        margin={{ top: 10, bottom: 30, left: 40, right: 10 }}
                        slotProps={{ legend: { hidden: true } }}
                        sx={{
                            '& .MuiChartsAxis-line': { stroke: '#e0e0e0' },
                            '& .MuiChartsAxis-tick': { stroke: '#e0e0e0' },
                        }}
                    />
                </>
            )}
        </DialogContent>
    </Dialog>
);

const SectionCard = ({ children, sx = {}, ...props }) => (
    <Box sx={{
        bgcolor: 'white',
        borderRadius: 3,
        p: { xs: 2.5, md: 3 },
        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
        mb: 3,
        ...sx,
    }} {...props}>
        {children}
    </Box>
);

const SEVERITY_STYLES = {
    high:     { bg: '#FFF3F0', border: '#FFCCBC', dot: '#E53935', titleColor: '#B71C1C' },
    medium:   { bg: '#FFFDE7', border: '#FFE082', dot: '#F9A825', titleColor: '#E65100' },
    positive: { bg: '#F1F8E9', border: '#C5E1A5', dot: '#43A047', titleColor: '#1B5E20' },
};

const InsightCard = ({ insight }) => {
    const s = SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.medium;
    return (
        <Box sx={{
            display: 'flex',
            gap: 1.5,
            p: 1.75,
            borderRadius: 2,
            bgcolor: s.bg,
            border: `1px solid ${s.border}`,
            mb: 1.5,
            '&:last-child': { mb: 0 },
        }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.dot, mt: 0.6, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: s.titleColor, mb: 0.25 }}>
                    {insight.title}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#555', lineHeight: 1.5 }}>
                    {insight.message}
                </Typography>
            </Box>
        </Box>
    );
};

const StatisticsPage = () => {
    const { backendUrl, authTokens, profile } = useContext(AuthContext);
    const isPro = profile?.subscription_tier === 'pro';
    const location = useLocation();
    const [relationships, setRelationships] = useState([]);
    const [selectedRelationship, setSelectedRelationship] = useState('');
    const [chores, setChores] = useState([]);
    const [timeoffs, setTimeoffs] = useState([]);
    const [weightedScores, setWeightedScores] = useState({ user_weighted_score: 0, partner_weighted_score: 0 });
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [fromDate, setFromDate] = useState(dayjs().subtract(6, 'days').format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [selectedRange, setSelectedRange] = useState('Week');
    const [monthlyData, setMonthlyData] = useState([]);
    const [monthlyTimeoffData, setMonthlyTimeoffData] = useState([]);
    const [insights, setInsights] = useState([]);
    const [mode, setMode] = useState('compare'); // 'my' | 'compare'
    const [detailDialog, setDetailDialog] = useState({ open: false, title: '', data: [], loading: false });
    const [exporting, setExporting] = useState(false);
    const contentRef = useRef(null);

    useEffect(() => {
        fetchRelationships();
    }, [location.key]); // re-fetch whenever user navigates to this page

    const showError = (message) => setSnackbar({ open: true, message, severity: 'error' });

    const getEndRef = (rel) => {
        if (rel && !rel.active && rel.date_ended) return dayjs(rel.date_ended);
        return dayjs();
    };

    const fetchRelationships = async () => {
        try {
            const response = await fetch(`${backendUrl}/api/all-relationships/`, {
                headers: { Authorization: `Bearer ${authTokens.access}` },
                cache: 'no-store',
            });
            if (!response.ok) throw new Error('Failed to fetch relationships');
            const data = await response.json();
            setRelationships(data);

            const params = new URLSearchParams(location.search);
            const urlRelId = params.get('relationship_id');
            const targetRel = urlRelId ? data.find(r => r.relationship_id === urlRelId) : data.find(r => r.active);

            if (targetRel) {
                setSelectedRelationship(targetRel.relationship_id);
                const endRef = getEndRef(targetRel);
                const defaultRange = urlRelId && !targetRel.active ? 'Year' : 'Week';
                const start = defaultRange === 'Year'
                    ? endRef.subtract(364, 'days').format('YYYY-MM-DD')
                    : endRef.subtract(6, 'days').format('YYYY-MM-DD');
                const end = endRef.format('YYYY-MM-DD');
                setFromDate(start);
                setToDate(end);
                setSelectedRange(defaultRange);
                const hasPartner = !!targetRel.partner_id;
                const initialMode = hasPartner ? 'compare' : 'my';
                setMode(initialMode);
                fetchAll(targetRel.relationship_id, start, end, initialMode, defaultRange);
            }
        } catch (error) {
            showError(error.message);
        }
    };

    const fetchAll = async (relId, start, end, fetchMode = mode, range = selectedRange) => {
        if (!relId) return;
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${authTokens.access}` };
            const base = backendUrl;
            const modeParam = `&mode=${fetchMode}`;
            const isMultiMonth = MULTI_MONTH_RANGES.has(range);
            const currentRel = relationships.find(r => r.relationship_id === relId);
            const hasLinkedPartner = !!currentRel?.partner_id;

            const multiMonthMode = hasLinkedPartner ? 'compare' : fetchMode;
            const [choresRes, timeoffsRes, weightedRes, insightsRes, ...monthlyResponses] = await Promise.all([
                fetch(`${base}/api/chore-statistics/${relId}/?start_date=${start}&end_date=${end}${modeParam}`, { headers, cache: 'no-store' }),
                fetch(`${base}/api/timeoff-statistics/${relId}/?start_date=${start}&end_date=${end}${modeParam}`, { headers, cache: 'no-store' }),
                fetch(`${base}/api/weighted-chore-statistics/${relId}/?start_date=${start}&end_date=${end}${modeParam}`, { headers, cache: 'no-store' }),
                fetch(`${base}/api/insights/${relId}/?start_date=${start}&end_date=${end}`, { headers, cache: 'no-store' }),
                ...(isMultiMonth ? [
                    fetch(`${base}/api/monthly-weighted-statistics/${relId}/?start_date=${start}&end_date=${end}&mode=${multiMonthMode}`, { headers, cache: 'no-store' }),
                    fetch(`${base}/api/monthly-timeoff-statistics/${relId}/?start_date=${start}&end_date=${end}&mode=${multiMonthMode}`, { headers, cache: 'no-store' }),
                ] : []),
            ]);

            const [monthlyRes, monthlyTimeoffRes] = monthlyResponses;

            if (!choresRes.ok) throw new Error('Failed to fetch chore statistics');
            if (!timeoffsRes.ok) throw new Error('Failed to fetch timeoff statistics');
            if (!weightedRes.ok) throw new Error('Failed to fetch weighted statistics');
            if (monthlyRes && !monthlyRes.ok) throw new Error('Failed to fetch monthly statistics');
            if (monthlyTimeoffRes && !monthlyTimeoffRes.ok) throw new Error('Failed to fetch monthly time-off statistics');

            const [choresData, timeoffsData, weightedData] = await Promise.all([
                choresRes.json(),
                timeoffsRes.json(),
                weightedRes.json(),
            ]);
            const insightsData = insightsRes.ok ? await insightsRes.json() : [];
            const monthlyRaw = monthlyRes ? await monthlyRes.json() : [];
            const monthlyTimeoffRaw = monthlyTimeoffRes ? await monthlyTimeoffRes.json() : [];

            setChores(choresData);
            setTimeoffs(timeoffsData);
            setWeightedScores(weightedData);
            setInsights(insightsData);
            setMonthlyData(monthlyRaw);
            setMonthlyTimeoffData(monthlyTimeoffRaw);
        } catch (error) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTimeRange = (range) => {
        const rel = relationships.find(r => r.relationship_id === selectedRelationship);
        const endRef = getEndRef(rel);
        let startDate;
        switch (range) {
            case 'Week': startDate = endRef.subtract(6, 'days'); break;
            case 'Month': startDate = endRef.subtract(29, 'days'); break;
            case '3 Months': startDate = endRef.subtract(89, 'days'); break;
            case '6 Months': startDate = endRef.subtract(179, 'days'); break;
            case 'Year': startDate = endRef.subtract(364, 'days'); break;
            case 'All': {
                startDate = rel ? dayjs(rel.date_started) : endRef.subtract(10, 'years');
                break;
            }
            default: return;
        }
        const start = startDate.format('YYYY-MM-DD');
        const end = endRef.format('YYYY-MM-DD');
        if (!MULTI_MONTH_RANGES.has(range)) { setMonthlyData([]); setMonthlyTimeoffData([]); }
        setFromDate(start);
        setToDate(end);
        setSelectedRange(range);
        fetchAll(selectedRelationship, start, end, mode, range);
    };

    const handleModeChange = (_, newMode) => {
        if (!newMode) return;
        setMode(newMode);
        if (fromDate && toDate) fetchAll(selectedRelationship, fromDate, toDate, newMode, selectedRange);
    };

    const handleShowMonthly = async (id, name, type) => {
        setDetailDialog({ open: true, title: name, data: [], loading: true });
        try {
            const endpoint = type === 'chore' ? 'monthly-chore-detail' : 'monthly-timeoff-detail';
            const param = type === 'chore' ? 'chore_id' : 'timeoff_id';
            const url = `${backendUrl}/api/${endpoint}/${selectedRelationship}/?${param}=${id}&start_date=${fromDate}&end_date=${toDate}&mode=${mode}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${authTokens.access}` }, cache: 'no-store' });
            if (!res.ok) throw new Error('Failed');
            const data = await res.json();
            setDetailDialog({ open: true, title: name, data, loading: false });
        } catch {
            setDetailDialog(s => ({ ...s, loading: false }));
        }
    };

    const handleExportPDF = async () => {
        if (!contentRef.current) return;
        setExporting(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 50));
            const sections = Array.from(contentRef.current.querySelectorAll('[data-pdf-section]'));
            if (!sections.length) return;

            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const usableWidth = pageWidth - margin * 2;
            const gap = 4;
            let currentY = margin;

            for (let i = 0; i < sections.length; i++) {
                const canvas = await html2canvas(sections[i], {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                });
                const imgData = canvas.toDataURL('image/png');
                const sectionH = (canvas.height * usableWidth) / canvas.width;

                if (i > 0 && currentY + sectionH > pageHeight - margin) {
                    pdf.addPage();
                    currentY = margin;
                }
                pdf.addImage(imgData, 'PNG', margin, currentY, usableWidth, sectionH);
                currentY += sectionH + gap;
            }

            pdf.save(`statistics-${fromDate}-to-${toDate}.pdf`);
        } catch {
            showError('Failed to export PDF');
        } finally {
            setExporting(false);
        }
    };

    const selectedRel = relationships.find(r => r.relationship_id === selectedRelationship);
    const isShared = !!selectedRel?.partner_id;
    const partnerName = selectedRel?.partner_name || 'Partner';
    const partnerLabel = partnerName.split(' ')[0];

    const visibleChores = chores.filter(c => (c.user_value || 0) > 0 || (c.partner_value || 0) > 0);
    const visibleTimeoffs = timeoffs.filter(t => (t.user_value || 0) > 0 || (t.partner_value || 0) > 0);
    const userTimeoffTotal = visibleTimeoffs.reduce((sum, t) => sum + (t.user_value || 0), 0);
    const partnerTimeoffTotal = visibleTimeoffs.reduce((sum, t) => sum + (t.partner_value || 0), 0);
    const hasData = visibleChores.length > 0 || visibleTimeoffs.length > 0 ||
        weightedScores.user_weighted_score > 0 || weightedScores.partner_weighted_score > 0;

    const btnStyle = (active) => ({
        borderRadius: 2,
        px: 2,
        py: 0.75,
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.85rem',
        minWidth: 0,
        ...(active ? {
            bgcolor: USER_COLOR,
            color: 'white',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#6A5A7A', boxShadow: 'none' },
        } : {
            borderColor: '#ccc',
            color: '#555',
            '&:hover': { borderColor: USER_COLOR, color: USER_COLOR, bgcolor: 'rgba(123,107,141,0.04)' },
        }),
    });

    return (
        <>
        <SEO title="Statistics - Couplendar" />
        <Box sx={{ minHeight: '100vh', bgcolor: '#F9F7F4', py: { xs: 3, md: 4 } }}>
            <Container maxWidth="md" ref={contentRef}>
                {/* Header */}
                <Box data-pdf-section sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', bgcolor: '#F9F7F4', py: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: USER_COLOR }}>
                            Statistics
                        </Typography>
                        {hasData && !loading && (
                            <Typography sx={{ fontSize: '0.8rem', color: '#888', mt: 0.25 }}>
                                {selectedRel?.partner_name}{selectedRel?.active ? ' (Active)' : ''} &nbsp;·&nbsp; {fromDate} → {toDate}
                                {isShared ? ` · ${mode === 'compare' ? 'Compare with Partner' : 'My View'}` : ''}
                            </Typography>
                        )}
                    </Box>
                    {hasData && !loading && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={exporting ? <CircularProgress size={14} sx={{ color: '#555' }} /> : <PictureAsPdfOutlinedIcon />}
                            onClick={handleExportPDF}
                            disabled={exporting}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.85rem',
                                borderColor: '#ccc',
                                color: '#555',
                                '&:hover': { borderColor: USER_COLOR, color: USER_COLOR, bgcolor: 'rgba(123,107,141,0.04)' },
                                '&.Mui-disabled': { borderColor: '#ccc', color: '#999' },
                            }}
                        >
                            {exporting ? 'Exporting…' : 'Export PDF'}
                        </Button>
                    )}
                </Box>

                {/* Mode Toggle */}
                {isShared && <SectionCard sx={{ mb: 3, p: 0, overflow: 'hidden' }}>
                    <ToggleButtonGroup
                        value={mode}
                        exclusive
                        onChange={handleModeChange}
                        fullWidth
                        sx={{ borderRadius: 3 }}
                    >
                        <ToggleButton
                            value="my"
                            sx={{
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                border: 'none',
                                borderRadius: '12px 0 0 12px !important',
                                '&.Mui-selected': {
                                    bgcolor: USER_COLOR,
                                    color: 'white',
                                    '&:hover': { bgcolor: '#6A5A7A' },
                                },
                            }}
                        >
                            My View
                            <Box component="span" sx={{ ml: 1, fontSize: '0.75rem', opacity: 0.75 }}>
                                (all items)
                            </Box>
                        </ToggleButton>
                        <ToggleButton
                            value="compare"
                            sx={{
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                border: 'none',
                                borderRadius: '0 12px 12px 0 !important',
                                '&.Mui-selected': {
                                    bgcolor: USER_COLOR,
                                    color: 'white',
                                    '&:hover': { bgcolor: '#6A5A7A' },
                                },
                            }}
                        >
                            Compare with Partner
                            <Box component="span" sx={{ ml: 1, fontSize: '0.75rem', opacity: 0.75 }}>
                                (shared only)
                            </Box>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </SectionCard>}

                {/* Filters */}
                <SectionCard>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Relationship</InputLabel>
                        <Select
                            label="Relationship"
                            value={selectedRelationship}
                            onChange={(e) => {
                                const newRelId = e.target.value;
                                setSelectedRelationship(newRelId);
                                const rel = relationships.find(r => r.relationship_id === newRelId);
                                if (rel) {
                                    const endRef = getEndRef(rel);
                                    const start = endRef.subtract(6, 'days').format('YYYY-MM-DD');
                                    const end = endRef.format('YYYY-MM-DD');
                                    setFromDate(start);
                                    setToDate(end);
                                    setSelectedRange('Week');
                                    const newMode = rel.partner_id ? 'compare' : 'my';
                                    setMode(newMode);
                                    fetchAll(newRelId, start, end, newMode, 'Week');
                                }
                            }}
                            sx={{ borderRadius: 2 }}
                        >
                            {relationships.map((rel) => (
                                <MenuItem key={rel.relationship_id} value={rel.relationship_id}>
                                    {rel.partner_name}
                                    {rel.active && (
                                        <Chip label="Active" size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem', bgcolor: 'rgba(76,175,80,0.1)', color: 'green' }} />
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Range buttons */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {(isPro ? TIME_RANGES_PRO : TIME_RANGES_FREE).map((range) => (
                            <Button
                                key={range}
                                variant={selectedRange === range ? 'contained' : 'outlined'}
                                onClick={() => handleTimeRange(range)}
                                sx={btnStyle(selectedRange === range)}
                            >
                                {range}
                            </Button>
                        ))}
                    </Box>
                </SectionCard>

                {/* Loading */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress sx={{ color: USER_COLOR }} />
                    </Box>
                )}

                {/* Content */}
                {!loading && selectedRelationship && (
                    <>
                        {/* Weighted Contribution */}
                        {(weightedScores.user_weighted_score > 0 || weightedScores.partner_weighted_score > 0) && (
                            <SectionCard data-pdf-section>
                                <Typography sx={{ fontWeight: 600, color: '#222', mb: 2, fontSize: '1rem' }}>
                                    Weighted Contribution
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <ScoreCard
                                        label="You"
                                        value={weightedScores.user_weighted_score}
                                        unit="Weighted Points"
                                        color={USER_COLOR}
                                    />
                                    <ScoreCard
                                        label={partnerLabel}
                                        value={weightedScores.partner_weighted_score}
                                        unit="Weighted Points"
                                        color={PARTNER_COLOR}
                                    />
                                </Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#999', textAlign: 'center' }}>
                                    Simple chores count as their weight value. Time-tracked chores have a weight of 2, distributed proportionally.
                                </Typography>
                            </SectionCard>
                        )}

                        {/* Monthly Progress */}
                        {monthlyData.length > 1 && (
                            <SectionCard data-pdf-section>
                                <Typography sx={{ fontWeight: 600, color: '#222', mb: 0.5, fontSize: '1rem' }}>
                                    Monthly Progress
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: '#999', mb: 2 }}>
                                    Weighted contribution scores per month
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: USER_COLOR }} />
                                        <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>You</Typography>
                                    </Box>
                                    {(isShared || mode === 'my') && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: PARTNER_COLOR }} />
                                            <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>{partnerLabel}</Typography>
                                        </Box>
                                    )}
                                </Box>
                                <BarChart
                                    height={220}
                                    series={[
                                        {
                                            data: monthlyData.map(d => d.user_score),
                                            label: 'You',
                                            color: USER_COLOR,
                                            id: 'user',
                                        },
                                        ...((isShared || mode === 'my') ? [{
                                            data: monthlyData.map(d => d.partner_score),
                                            label: partnerLabel,
                                            color: PARTNER_COLOR,
                                            id: 'partner',
                                        }] : []),
                                    ]}
                                    xAxis={[{
                                        data: monthlyData.map(d => d.month),
                                        scaleType: 'band',
                                        tickLabelStyle: { fontSize: 11 },
                                    }]}
                                    yAxis={[{ tickLabelStyle: { fontSize: 11 } }]}
                                    margin={{ top: 10, bottom: 30, left: 40, right: 10 }}
                                    slotProps={{ legend: { hidden: true } }}
                                    sx={{
                                        '& .MuiChartsAxis-line': { stroke: '#e0e0e0' },
                                        '& .MuiChartsAxis-tick': { stroke: '#e0e0e0' },
                                    }}
                                />
                            </SectionCard>
                        )}

                        {/* Chores */}
                        {visibleChores.length > 0 && (
                            <SectionCard data-pdf-section>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                                    <Typography sx={{ fontWeight: 600, color: '#222', fontSize: '1rem' }}>
                                        Chores
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: USER_COLOR }} />
                                            <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>You</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PARTNER_COLOR }} />
                                            <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>{partnerLabel}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {visibleChores.map((chore) => (
                                    <ChoreComparisonRow
                                        key={chore.chore_id}
                                        chore={chore}
                                        partnerLabel={partnerLabel}
                                        onShowMonthly={MULTI_MONTH_RANGES.has(selectedRange) ? handleShowMonthly : undefined}
                                    />
                                ))}
                            </SectionCard>
                        )}

                        {/* Total Time Off Hours */}
                        {visibleTimeoffs.length > 0 && (userTimeoffTotal > 0 || partnerTimeoffTotal > 0) && (
                            <SectionCard data-pdf-section>
                                <Typography sx={{ fontWeight: 600, color: '#222', mb: 2, fontSize: '1rem' }}>
                                    Total Time Off Hours
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <ScoreCard
                                        label="You"
                                        value={userTimeoffTotal.toFixed(1)}
                                        unit="Total Hours"
                                        color={USER_COLOR}
                                    />
                                    <ScoreCard
                                        label={partnerLabel}
                                        value={partnerTimeoffTotal.toFixed(1)}
                                        unit="Total Hours"
                                        color={PARTNER_COLOR}
                                    />
                                </Box>
                            </SectionCard>
                        )}

                        {/* Time-off */}
                        {visibleTimeoffs.length > 0 && (
                            <SectionCard data-pdf-section>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                                    <Typography sx={{ fontWeight: 600, color: '#222', fontSize: '1rem' }}>
                                        Time Off
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: USER_COLOR }} />
                                            <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>You</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PARTNER_COLOR }} />
                                            <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>{partnerLabel}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {visibleTimeoffs.map((timeoff) => (
                                    <TimeoffComparisonRow
                                        key={timeoff.timeoff_id}
                                        timeoff={timeoff}
                                        partnerLabel={partnerLabel}
                                        onShowMonthly={MULTI_MONTH_RANGES.has(selectedRange) ? handleShowMonthly : undefined}
                                    />
                                ))}
                            </SectionCard>
                        )}

                        {/* Monthly Time-off Progress */}
                        {monthlyTimeoffData.length > 1 && (
                            <SectionCard data-pdf-section>
                                <Typography sx={{ fontWeight: 600, color: '#222', mb: 0.5, fontSize: '1rem' }}>
                                    Monthly Time-off
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: '#999', mb: 2 }}>
                                    Total time-off hours per month
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: USER_COLOR }} />
                                        <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>You</Typography>
                                    </Box>
                                    {(isShared || mode === 'my') && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: PARTNER_COLOR }} />
                                            <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>{partnerLabel}</Typography>
                                        </Box>
                                    )}
                                </Box>
                                <BarChart
                                    height={220}
                                    series={[
                                        {
                                            data: monthlyTimeoffData.map(d => d.user_total),
                                            label: 'You',
                                            color: USER_COLOR,
                                            id: 'user',
                                        },
                                        ...((isShared || mode === 'my') ? [{
                                            data: monthlyTimeoffData.map(d => d.partner_total),
                                            label: partnerLabel,
                                            color: PARTNER_COLOR,
                                            id: 'partner',
                                        }] : []),
                                    ]}
                                    xAxis={[{
                                        data: monthlyTimeoffData.map(d => d.month),
                                        scaleType: 'band',
                                        tickLabelStyle: { fontSize: 11 },
                                    }]}
                                    yAxis={[{ tickLabelStyle: { fontSize: 11 } }]}
                                    margin={{ top: 10, bottom: 30, left: 40, right: 10 }}
                                    slotProps={{ legend: { hidden: true } }}
                                    sx={{
                                        '& .MuiChartsAxis-line': { stroke: '#e0e0e0' },
                                        '& .MuiChartsAxis-tick': { stroke: '#e0e0e0' },
                                    }}
                                />
                            </SectionCard>
                        )}

                        {/* Insights */}
                        {insights.length > 0 && (
                            <SectionCard data-pdf-section>
                                <Typography sx={{ fontWeight: 600, color: '#222', mb: 0.5, fontSize: '1rem' }}>
                                    Insights
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: '#999', mb: 2 }}>
                                    {selectedRange ? `${fromDate} → ${toDate}` : ''}
                                </Typography>
                                {insights.map((insight, i) => (
                                    <InsightCard key={i} insight={insight} />
                                ))}
                            </SectionCard>
                        )}

                        {/* No data */}
                        {!hasData && (
                            <SectionCard sx={{ textAlign: 'center', py: 5 }}>
                                <Typography sx={{ fontSize: '2rem', mb: 1.5 }}>📊</Typography>
                                <Typography sx={{ color: '#888', fontWeight: 500, mb: 0.5 }}>
                                    No data for this period
                                </Typography>
                                <Typography sx={{ color: '#aaa', fontSize: '0.85rem' }}>
                                    {mode === 'compare'
                                        ? 'Share chores and time-off with your partner to see comparisons here.'
                                        : 'No activity recorded for this period.'}
                                </Typography>
                            </SectionCard>
                        )}
                    </>
                )}

                {/* No relationship selected */}
                {!loading && !selectedRelationship && (
                    <SectionCard sx={{ textAlign: 'center', py: 5 }}>
                        <Typography sx={{ color: '#888' }}>
                            Select a relationship to view statistics.
                        </Typography>
                    </SectionCard>
                )}
            </Container>

            <MonthlyDetailDialog
                open={detailDialog.open}
                onClose={() => setDetailDialog(s => ({ ...s, open: false }))}
                title={detailDialog.title}
                data={detailDialog.data}
                loading={detailDialog.loading}
                partnerLabel={partnerLabel}
                showPartner={isShared || mode === 'my'}
            />

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
        </>
    );
};

export default StatisticsPage;
